import {
  ModalDialog,
  RadioFieldController,
  Stack,
  TextAreaFieldController,
  TextInputFieldController,
  ViewItemField,
} from '@fukuri-admins/ui/common';

type Props = {
  /**
   * ダイアログを閉じるコールバック
   */
  onClose: () => void;
  /**
   * フォーム送信ハンドラ
   */
  onSubmit: React.ComponentProps<'form'>['onSubmit'];
  /** 利用企業名 */
  name: string;
  /** 取引店舗名（未設定の場合は '-'） */
  partnerBranchName: string;
  /** 担当者名（未設定の場合は '-'） */
  contactPersonName: string;
  /** 契約プラン名（未設定の場合は '-'） */
  contractPlanName: string;
  /** 利用者数 */
  userCount: number;
  /**
   * マスタ取引店舗一覧（GET /api/v1/{threeLetterCode}/partner_admins/partner_branches）に
   * 1件以上の取引店舗が存在するか（PEN-985）
   *
   * true のときのみ表示情報の「取引店舗」項目を表示する（PEN-989）
   */
  hasPartnerBranch: boolean;
  /**
   * 削除ボタンを disabled にするか
   *
   * - 種別ラジオが選択済み かつ 削除する利用企業名が一致したとき false
   */
  isConfirmDisabled: boolean;
};

/**
 * 利用企業削除ダイアログ Presentation
 *
 * - 削除対象の表示情報（5項目）を props で受け取り表示する（PEN-715 T07d）
 * - T07e（PEN-743）: 共通フォーム化（discriminated union 廃止）
 * - T08（PEN-699）: バリデーション・エラー表示・DELETE API 送信・削除ボタン活性制御を実装
 * - hasPartnerBranch のときのみ「取引店舗」項目を表示する（PEN-985 / PEN-989）
 */
export const DeleteClientDialogPresentation: React.FC<Props> = ({
  onClose,
  onSubmit,
  name,
  partnerBranchName,
  contactPersonName,
  contractPlanName,
  userCount,
  hasPartnerBranch,
  isConfirmDisabled,
}) => (
  <ModalDialog
    isOpen
    title='この企業を削除しますか？'
    description='利用企業を削除すると、紐づくユーザーごと削除されます。一度削除すると元には戻せません。'
    onClose={onClose}
    onConfirm={onSubmit}
    confirmText='削除'
    confirmVariant='destructive'
    isConfirmDisabled={isConfirmDisabled}
    variant='lg'
  >
    <form onSubmit={onSubmit}>
      <Stack direction='column' gap={16}>
        {/* 表示情報5項目 */}
        <Stack direction='column' gap={16}>
          <ViewItemField label='利用企業' direction='row' value={name} />
          {hasPartnerBranch && (
            <ViewItemField label='取引店舗' direction='row' value={partnerBranchName} />
          )}
          <ViewItemField label='担当者' direction='row' value={contactPersonName} />
          <ViewItemField label='契約プラン' direction='row' value={contractPlanName} />
          <ViewItemField label='利用者数' direction='row' value={String(userCount)} />
        </Stack>

        {/* 種別ラジオ */}
        <RadioFieldController
          name='type'
          fieldWrapperProps={{
            labelText: '種別',
            labelType: 'required',
          }}
          radioGroup={{
            options: [
              { value: 'churned', label: '解約' },
              { value: 'created_by_mistake', label: '誤登録' },
            ],
            direction: 'column',
            gap: 4,
          }}
        />

        {/* 削除理由（任意） */}
        <TextAreaFieldController
          name='reason'
          fieldWrapperProps={{
            labelText: '削除理由',
          }}
          textArea={{
            placeholder: '削除理由を入力',
          }}
        />

        {/* 削除する利用企業名（必須・利用企業名と完全一致で削除ボタンが活性化） */}
        <TextInputFieldController
          name='clientName'
          fieldWrapperProps={{
            labelText: '削除する利用企業名',
            labelType: 'required',
            beforeMessage: 'このまま削除する場合は、削除する利用企業名を入力してください。',
          }}
          textInput={{
            type: 'text',
            placeholder: '利用企業名を入力',
          }}
        />
      </Stack>
    </form>
  </ModalDialog>
);
